import { useEffect, useRef } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { Typography } from 'Components/Typography';

import { genitive } from 'utils/textUtils';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import type { ImageType, Ingredient } from './SampleFormulationCardCarousel';

const Wrapper = styled.div<{
  height: number | undefined;
  opacity: number;
}>`
  border-radius: 10px;
  background-color: ${theme.colors.neutral[100]};
  align-items: center;
  flex-direction: column;
  display: flex;
  opacity: ${({ opacity }) => opacity};
  transition: opacity 500ms;
  justify-content: space-between;
  height: ${({ height }) => `${height}px` || undefined};
  ${legacyTheme.breakpoints.up('md')} {
    justify-content: center;
    height: inherit;
  }
`;

const CustomerInfosWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const Title = styled(Typography)`
  font-size: 20px;
  ${legacyTheme.breakpoints.up('md')} {
    font-size: 28px;
  }
`;

const Description = styled(Typography)<{
  variant: string;
}>`
  ${legacyTheme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;

const CustomerImage = styled(Image)<{ selected: boolean }>`
  width: 94px;
  height: 130px;
  flex: 1;
  object-fit: cover;
  border-radius: 10px 0 0;
  ${legacyTheme.breakpoints.up('md')} {
    width: ${({ selected }) => (selected ? 167 : 149)}px;
    height: ${({ selected }) => (selected ? 167 : 149)}px;
    transition:
      width 300ms,
      height 300ms;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex: 3;
  padding: 0 20px;
  flex-direction: column;
`;

const ProfileSection = styled.div<{ selected: boolean }>`
  background-color: ${theme.colors.neutral[200]};
  width: 100%;
  padding: 25px;
  ${legacyTheme.breakpoints.up('lg')} {
    padding: ${({ selected }) => (selected ? 20 : 10)}px;
    transition: padding 300ms;
  }
`;

const ProfileTitle = styled(Typography)`
  font-weight: 600;
  font-size: 14px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.neutral[800]};
  margin: 16px 0;
`;

const IngredientsWrapper = styled.div<{ selected: boolean }>`
  width: 100%;
  padding: 25px;
  ${legacyTheme.breakpoints.up('lg')} {
    padding: ${({ selected }) => (selected ? 20 : 10)}px;
    transition: padding 300ms;
  }
`;

const IngredientWrapper = styled.div`
  display: flex;
`;

const IngredientImage = styled(Image)`
  width: 55px;
  height: 55px;
  object-fit: contain;
`;

const FlexDirectionSwitch = styled.div`
  display: flex;
  flex-direction: column;
  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: row;
  }
`;

const FlexBox = styled.div<{ flex: number }>`
  display: flex;
  flex: ${({ flex }) => flex};
`;

const IngredientDescriptionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 2;
  justify-content: space-between;
`;

const AnimatedTypography = styled(Typography)<{ selected: boolean }>`
  ${legacyTheme.breakpoints.up('md')} {
    padding: 0 10px;
    font-size: ${({ selected }) => (selected ? 14 : 12)}px;
    transition: font-size 200ms;
  }
`;

const IngredientsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 8px;
  ${legacyTheme.breakpoints.up('md')} {
    display: contents;
    padding-right: 0;
  }
`;

const IngredientLabel = styled(Typography)`
  ${legacyTheme.breakpoints.up('md')} {
    font-size: 14px;
  }
`;

type SampleFormulationCardProps = {
  selected: boolean;
  setHeight: (height: number | undefined) => void;
  height?: number;
  onClick: () => void;
  opacity: number;
  title: string;
  description: React.ReactNode;
  image: ImageType;
  concerns: string;
  environment: string;
  ingredients: Ingredient[];
};

const SampleFormulationCard = ({
  concerns,
  description,
  title,
  image,
  environment,
  ingredients,
  selected,
  opacity,
  onClick,
  setHeight,
  height,
}: SampleFormulationCardProps) => {
  const { isDesktop, isMobile } = useResponsiveVariant();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile) {
      setHeight(wrapperRef?.current?.clientHeight);
    }
  }, [wrapperRef, isMobile]);

  return (
    <Wrapper
      ref={wrapperRef}
      data-click="swipe-next"
      data-from="sample-formula-card"
      height={height}
      onClick={onClick}
      opacity={opacity}
    >
      <div style={{ width: '100%' }}>
        <CustomerInfosWrapper>
          <CustomerImage alt={image.alt} selected={selected} src={image.src} />
          <TextWrapper>
            <Title medium variant="p1">
              {title}
            </Title>

            <Description variant="p1">{description}</Description>
          </TextWrapper>
        </CustomerInfosWrapper>
        <ProfileSection selected={selected}>
          <FlexDirectionSwitch>
            <FlexBox flex={1}>
              <ProfileTitle variant="mono1">TOP SKIN CONCERNS</ProfileTitle>
            </FlexBox>
            <FlexBox flex={2}>
              <AnimatedTypography color="grey" selected={selected} variant="p1">
                {concerns}
              </AnimatedTypography>
            </FlexBox>
          </FlexDirectionSwitch>
          <Separator />
          <FlexDirectionSwitch>
            <FlexBox flex={1}>
              <ProfileTitle variant="mono1">ENVIRONMENT</ProfileTitle>
            </FlexBox>
            <FlexBox flex={2}>
              <AnimatedTypography color="grey" selected={selected} variant="p1">
                {environment}
              </AnimatedTypography>
            </FlexBox>
          </FlexDirectionSwitch>
        </ProfileSection>
      </div>
      <IngredientsWrapper selected={selected}>
        <ProfileTitle variant="mono1">{`${genitive(title)} KEY INGREDIENTS`}</ProfileTitle>
        {ingredients.map((ingredient) => (
          <div key={ingredient.label}>
            <Separator />
            <IngredientWrapper>
              <IngredientsContainer>
                <FlexBox flex={1}>
                  <IngredientLabel color="grey" medium variant="p1">
                    {ingredient.label}
                  </IngredientLabel>
                </FlexBox>
                <IngredientDescriptionWrapper>
                  <AnimatedTypography color="grey" selected={selected} variant="p1">
                    {ingredient.description}
                  </AnimatedTypography>
                  {isDesktop && <IngredientImage alt={ingredient.label} src={ingredient.image} />}
                </IngredientDescriptionWrapper>
              </IngredientsContainer>
              {!isDesktop && <IngredientImage alt={ingredient.label} src={ingredient.image} />}
            </IngredientWrapper>
          </div>
        ))}
      </IngredientsWrapper>
    </Wrapper>
  );
};

export default SampleFormulationCard;
